import * as React from "react";
import { useNavigate, useParams } from "react-router";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { HelpArticleView } from "@volley/data";

import fetchApi, { logFetchError } from "../../util/fetchApi";
import { recordPromptEvent } from "../AskVolley/api";
import { loadChatSession } from "../AskVolley/chatSession";
import SupportLink from "../common/SupportLink";
import { useCurrentUser } from "../hooks/currentUser";

import ArticleBody from "./ArticleBody";

/**
 * Reader for a single help article, reached from an Ask Volley help result.
 *
 * The endpoint only serves PUBLISHED articles, so an unreviewed or rejected
 * slug 404s here exactly as an unknown one does.
 */
export default function HelpArticle(): React.JSX.Element {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const [article, setArticle] = React.useState<HelpArticleView | null>(null);
    const [loading, setLoading] = React.useState(true);

    // The Ask Volley ask this read belongs to, when there is one. Tapping a help
    // card saves the open chat before navigating, so the snapshot carries the
    // prompt and impression the answer was served against — which is what lets
    // the read and any escalation join the same funnel as the question.
    //
    // Attributed only when this very article was one of the cards served, so a
    // deep link or a bookmark opened while an unrelated chat is still saved is
    // left unattributed rather than credited to the wrong ask.
    const askContext = React.useMemo(() => {
        const snapshot = loadChatSession(currentUser?.id);
        if (!snapshot || !slug) return null;
        const wasServed = snapshot.thread.some(
            (message) =>
                message.kind === "carousel" &&
                message.cards.some(
                    (card) =>
                        card.type === "HELP_ARTICLE" &&
                        (card.metadata as { slug?: string } | null)?.slug ===
                            slug,
                ),
        );
        return wasServed ? snapshot : null;
    }, [currentUser?.id, slug]);

    // One view per article opened, not per render.
    const viewRecorded = React.useRef(false);
    React.useEffect(() => {
        if (viewRecorded.current || !article || !askContext) return;
        viewRecorded.current = true;
        recordPromptEvent({
            eventType: "interaction",
            prompt: askContext.prompt,
            impressionUlid: askContext.impressionUlid,
            sportId: askContext.sportId,
            metadata: { action: "help_article_view", slug: article.slug },
        });
    }, [article, askContext]);

    const recordEscalation = React.useCallback(
        (reason: "read_article" | "article_missing") => {
            if (!askContext) return;
            recordPromptEvent({
                eventType: "interaction",
                prompt: askContext.prompt,
                impressionUlid: askContext.impressionUlid,
                sportId: askContext.sportId,
                metadata: {
                    action: "support_escalate",
                    source: "help_article",
                    reason,
                    slug,
                },
            });
        },
        [askContext, slug],
    );

    React.useEffect(() => {
        let cancelled = false;
        void (async () => {
            setLoading(true);
            try {
                const result = await fetchApi<HelpArticleView>(
                    `/api/help-articles/slug/${encodeURIComponent(slug ?? "")}`,
                );
                if (!cancelled) setArticle(result);
            } catch (e) {
                logFetchError(e, "Failed to fetch help article");
                if (!cancelled) setArticle(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    return (
        <Box sx={{ maxWidth: 720, mx: "auto", px: 2, pb: 6 }}>
            <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 1, py: 1.5 }}
            >
                <IconButton
                    aria-label="Back"
                    onClick={() => navigate(-1)}
                    edge="start"
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Help
                </Typography>
            </Stack>

            {loading && (
                <Stack sx={{ alignItems: "center", py: 6 }}>
                    <CircularProgress size={28} />
                </Stack>
            )}

            {!loading && !article && (
                <Box sx={{ py: 4 }}>
                    <Typography variant="body1">
                        We couldn&apos;t find that article.
                    </Typography>
                    <SupportLink
                        message={askContext?.freeTextSeed}
                        onEscalate={() => recordEscalation("article_missing")}
                        TypographyProps={{ align: "left" }}
                    >
                        Message support
                    </SupportLink>
                </Box>
            )}

            {!loading && article && (
                <>
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{ fontWeight: 700, mb: 2 }}
                    >
                        {article.title}
                    </Typography>
                    <ArticleBody html={article.bodyHtml} />
                    <SupportLink
                        preText="Still stuck?"
                        message={askContext?.freeTextSeed}
                        onEscalate={() => recordEscalation("read_article")}
                        TypographyProps={{ align: "left", sx: { mt: 4 } }}
                    >
                        Message support
                    </SupportLink>
                </>
            )}
        </Box>
    );
}
